import React, { useCallback, useEffect, useState } from 'react';
import {
  App,
  Button,
  Col,
  Row,
  Spin,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

import ReassignLeadModal from './components/ReassignLeadModal';
import FollowUpModal from './components/FollowUpModal';
import LeadClosingRequestModal from './components/LeadClosingRequestModal';
import LeadClosingDetailModal from './components/LeadClosingDetailModal';
import LeadInfoSection from './components/LeadInfoSection';
import FollowUpActivitiesCard from './components/FollowUpActivitiesCard';

const { Title } = Typography;

const LeadDetail = () => {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const { message } = App.useApp();

  const [lead, setLead] = useState<any>(null);
  const [followUps, setFollowUps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Options for Follow-up
  const [followUpActions, setFollowUpActions] = useState<any[]>([]);
  const [followUpStatuses, setFollowUpStatuses] = useState<any[]>([]);
  const [followUpCategories, setFollowUpCategories] = useState<any[]>([]);

  // Modal Visibility States
  const [reassignModalVisible, setReassignModalVisible] = useState(false);
  const [followUpModalVisible, setFollowUpModalVisible] = useState(false);
  const [closingModalVisible, setClosingModalVisible] = useState(false);
  const [okDetailModalVisible, setOkDetailModalVisible] = useState(false);
  const [resubmitRequest, setResubmitRequest] = useState<any>(null);

  const fetchFollowUpOptions = useCallback(async () => {
    try {
      const [actionsRes, statusesRes, categoriesRes] = await Promise.all([
        api.get('lead-follow-up-actions'),
        api.get('lead-follow-up-status', { params: { is_active: true, is_system_only: false } }),
        api.get('lead-follow-up-categories')
      ]);

      if (actionsRes.data?.status === 'success') {
        setFollowUpActions(actionsRes.data.data.filter((item: any) => item.is_active));
      }
      if (statusesRes.data?.status === 'success') {
        setFollowUpStatuses(statusesRes.data.data.filter((item: any) => item.is_active && !item.is_system_only));
      }
      if (categoriesRes.data?.status === 'success') {
        setFollowUpCategories(categoriesRes.data.data.filter((item: any) => item.is_active));
      }
    } catch (error) {
      console.error('Failed to fetch follow-up options:', error);
      message.error('Gagal memuat opsi untuk form follow-up.');
    }
  }, [message]);

  const fetchLead = useCallback(async () => {
    try {
      const response = await api.get(`leads/${id}`);
      const leadData = response.data.data;
      setLead(leadData);
    } catch (error: any) {
      console.error('Failed to fetch lead:', error);
      message.error('Gagal memuat data lead');
      navigate('/leads/data');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, message]);

  const fetchFollowUps = useCallback(async () => {
    try {
      const response = await api.get(`leads/${id}/follow-ups`);
      if (response.data?.status === 'success') {
        setFollowUps(response.data.data || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch follow-ups:', error);
    }
  }, [id]);

  useEffect(() => {
    fetchLead();
    fetchFollowUpOptions();
    fetchFollowUps();
  }, [fetchLead, fetchFollowUpOptions, fetchFollowUps]);

  // Reassign / Assign Handlers
  const handleAssignSubmit = async (leadId: string, assignedToId: string) => {
    try {
      await api.post(`leads/${leadId}/assign`, { assigned_to_id: assignedToId });
      message.success('Lead berhasil di-assign');
      setReassignModalVisible(false);
      fetchLead();
    } catch (error: any) {
      console.error('Failed to assign lead:', error);
      message.error(error.response?.data?.message || 'Gagal meng-assign lead');
    }
  };

  const handleReassignSubmit = async (leadId: string, assignedToId: string, reason: string) => {
    try {
      await api.post(`leads/${leadId}/reassign`, { agent_id: assignedToId, reason: reason });
      message.success('Lead berhasil di-reassign');
      setReassignModalVisible(false);
      fetchLead();
    } catch (error: any) {
      console.error('Failed to reassign lead:', error);
      message.error(error.response?.data?.message || 'Gagal me-reassign lead');
    }
  };

  const handleOpenClosingRequestModal = (existingRequest?: any) => {
    setResubmitRequest(existingRequest || null);
    setClosingModalVisible(true);
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getPriorityColor = (priority: string) => {
    const colors: { [key: string]: string } = {
      low: 'green',
      medium: 'orange',
      high: 'red',
    };
    return colors[priority] || 'default';
  };

  const getFollowUpStatusColor = (status: string) => {
    if (!status) return 'default';
    const statusName = status.toLowerCase();
    if (statusName === 'hot') return 'error';
    if (statusName === 'rsv') return 'green';
    if (statusName === 'cold') return 'processing';
    if (statusName === 'o k') return 'success';
    return 'default';
  };

  const renderInterestStatusTag = (interestStatus: string) => {
    if (!interestStatus) return <Tag>-</Tag>;

    const formatToTitleCase = (str: string) => {
      if (!str) return '';
      return str
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    };

    const colorMap: { [key: string]: string } = {
      minat_pesan: 'success',
      minat_proses: 'blue',
      minat_tidak_pesan: 'warning',
      tidak_minat: 'error',
    };

    const color = colorMap[interestStatus] || 'default';
    const displayText = formatToTitleCase(interestStatus);

    return <Tag color={color}>{displayText}</Tag>;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!lead) {
    return <div>Lead tidak ditemukan</div>;
  }

  const isSalesUser = Boolean(user?.role?.name?.toLowerCase().includes('sales'));

  return (
    <div>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/leads/data')}
            style={{ marginRight: 16 }}
          >
            Kembali
          </Button>
          <Title level={2} style={{ margin: 0, display: 'inline' }}>
            {lead.full_name}
          </Title>
          <Tag color={getFollowUpStatusColor(lead.status_lead?.name)} style={{ marginLeft: 16 }}>
            {lead.status_lead?.name}
          </Tag>
          <Tag color={getFollowUpStatusColor(lead.lead_follow_up_status?.name)}>
            {lead.lead_follow_up_status?.name?.toUpperCase()}
          </Tag>
        </Col>
        <Col>
          {(hasPermission('lead.assign') || hasPermission('lead.reassigned')) && (
            <Button
              icon={<UserSwitchOutlined />}
              onClick={() => setReassignModalVisible(true)}
              style={{ marginRight: 8 }}
            >
              Assigned To
            </Button>
          )}
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/leads/${id}/edit`)}
          >
            Edit Lead
          </Button>
        </Col>
      </Row>

      {/* Lead Information & Quick Contact Section */}
      <LeadInfoSection
        lead={lead}
        formatCurrency={formatCurrency}
        getPriorityColor={getPriorityColor}
        renderInterestStatusTag={renderInterestStatusTag}
      />

      {/* Follow Up Activities Card */}
      <Row style={{ marginTop: 16 }}>
        <Col xs={24}>
          <FollowUpActivitiesCard
            lead={lead}
            user={user}
            hasPermission={hasPermission}
            followUps={followUps}
            followUpStatuses={followUpStatuses}
            isSalesUser={isSalesUser}
            onOpenFollowUpModal={() => setFollowUpModalVisible(true)}
            onOpenClosingRequestModal={handleOpenClosingRequestModal}
            onOpenClosingDetailModal={() => setOkDetailModalVisible(true)}
            formatCurrency={formatCurrency}
            getFollowUpStatusColor={getFollowUpStatusColor}
          />
        </Col>
      </Row>

      {/* Follow-Up Modal */}
      <FollowUpModal
        open={followUpModalVisible}
        onCancel={() => setFollowUpModalVisible(false)}
        onSuccess={() => {
          setFollowUpModalVisible(false);
          fetchLead();
          fetchFollowUps();
        }}
        leadId={id as string}
        followUps={followUps}
        followUpActions={followUpActions}
        followUpStatuses={followUpStatuses}
        followUpCategories={followUpCategories}
      />

      {/* Reassign Lead Modal */}
      <ReassignLeadModal
        visible={reassignModalVisible}
        onCancel={() => setReassignModalVisible(false)}
        lead={lead}
        onAssignSubmit={handleAssignSubmit}
        onReassignSubmit={handleReassignSubmit}
        message={message}
        hasPermission={hasPermission}
      />

      {/* Lead Closing Request Modal */}
      <LeadClosingRequestModal
        open={closingModalVisible}
        onCancel={() => setClosingModalVisible(false)}
        onSuccess={() => {
          setClosingModalVisible(false);
          fetchLead();
          fetchFollowUps();
        }}
        lead={lead}
        resubmitRequest={resubmitRequest}
      />

      {/* Modal Detail Informasi Closing OK */}
      <LeadClosingDetailModal
        open={okDetailModalVisible}
        onClose={() => setOkDetailModalVisible(false)}
        lead={lead}
        formatCurrency={formatCurrency}
      />
    </div>
  );
};

export default LeadDetail;
